import _extends from "@babel/runtime/helpers/esm/extends";
/** @jsx jsx */

import { jsx } from 'theme-ui';
import React from 'react';
import { useConfig } from 'docz';
import { LiveProvider, LiveError, LivePreview, LiveEditor } from 'react-live';
import { Resizable } from 're-resizable';
import copy from 'copy-text-to-clipboard';
import { Wrapper } from './Wrapper';
import { usePrismTheme } from '~utils/theme';
import * as styles from './styles';
import * as Icons from '../Icons';
import { jsx as ___EmotionJSX } from "@emotion/core";

const getResizableProps = (width, setWidth) => ({
  minWidth: 260,
  maxWidth: '100%',
  size: {
    width: width,
    height: 'auto'
  },
  style: {
    margin: 0,
    marginRight: 'auto'
  },
  enable: {
    top: false,
    right: true,
    bottom: false,
    left: false,
    topRight: false,
    bottomRight: false,
    bottomLeft: false,
    topLeft: false
  },
  onResizeStop: (e, direction, ref) => {
    setWidth(ref.style.width);
  }
});

const transformCode = code => {
  if (code.startsWith('()') || code.startsWith('class')) return code;
  return `<React.Fragment>${code}</React.Fragment>`;
};

export const Playground = ({
  code,
  scope,
  language,
  useScoping = false
}) => {
  const {
    themeConfig: {
      showPlaygroundEditor,
      showLiveError,
      showLivePreview
    }
  } = useConfig(); // Makes sure scope is only given on mount to avoid infinite re-render on hot reloads

  const [scopeOnMount] = React.useState(scope);
  const theme = usePrismTheme();
  const [showingCode, setShowingCode] = React.useState(showPlaygroundEditor);
  const [width, setWidth] = React.useState('100%');
  const resizableProps = getResizableProps(width, setWidth);

  const copyCode = () => copy(code);

  const toggleCode = () => setShowingCode(s => !s);

  return jsx(Resizable, _extends({}, resizableProps, {
    "data-testid": "playground"
  }), jsx(LiveProvider, {
    code: code,
    scope: scopeOnMount,
    transformCode: transformCode,
    language: language,
    theme: theme
  }, jsx("div", {
    sx: styles.previewWrapper
  }, jsx(Wrapper, {
    content: "preview",
    useScoping: useScoping,
    showingCode: showingCode
  }, showLivePreview && jsx(LivePreview, {
    sx: styles.preview,
    "data-testid": "live-preview"
  })), jsx("div", {
    sx: styles.buttons
  }, jsx("button", {
    sx: styles.button,
    onClick: copyCode
  }, jsx(Icons.Clipboard, {
    size: 12
  })), jsx("button", {
    sx: styles.button,
    onClick: toggleCode
  }, jsx(Icons.Code, {
    size: 12
  })))), showingCode && jsx(Wrapper, {
    content: "editor",
    useScoping: useScoping,
    showingCode: showingCode
  }, jsx("div", {
    sx: styles.editor(theme)
  }, jsx(LiveEditor, {
    "data-testid": "live-editor"
  }))), showLiveError && jsx(LiveError, {
    sx: styles.error,
    "data-testid": "live-error"
  })));
};

if (typeof Playground !== 'undefined' && Playground && Playground === Object(Playground) && Object.isExtensible(Playground) && !Playground.hasOwnProperty('__filemeta')) {
  Object.defineProperty(Playground, '__filemeta', {
    configurable: true,
    value: {
      name: "Playground",
      filename: "../../node_modules/gatsby-theme-docz/src/components/Playground/index.js"
    }
  });
}